import React from 'react';
import { Box, Typography, Chip, Tooltip, LinearProgress } from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import AssignmentIcon from '@mui/icons-material/Assignment';
import StarIcon from '@mui/icons-material/Star';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { useGameData } from '../context/GameProvider';

function healthColor(score: number): 'success' | 'warning' | 'error' {
  if (score >= 80) return 'success';
  if (score >= 60) return 'warning';
  return 'error';
}

const FortressHUD: React.FC = () => {
  const { fortressStatus } = useGameData();

  if (!fortressStatus) return null;

  const health_score = fortressStatus.health_score ?? 0;
  const active_quest_count = fortressStatus.active_quest_count ?? 0;
  const player = fortressStatus.player;
  const color = healthColor(health_score);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 0.5,
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Tooltip title={`Fortress health: ${health_score}%`}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 140 }}>
          <ShieldIcon fontSize="small" color={color} />
          <Typography variant="caption" color="text.secondary" noWrap>
            Fortress
          </Typography>
          <Box sx={{ flex: 1, mx: 0.5 }}>
            <LinearProgress
              variant="determinate"
              value={health_score}
              color={color}
              sx={{ height: 6, borderRadius: 1 }}
            />
          </Box>
          <Typography variant="caption" fontWeight="medium">
            {health_score}%
          </Typography>
        </Box>
      </Tooltip>

      <Tooltip title="Active quests">
        <Chip
          icon={<AssignmentIcon />}
          label={active_quest_count}
          size="small"
          variant="outlined"
          color={active_quest_count > 0 ? 'primary' : 'default'}
        />
      </Tooltip>

      {player && (
        <>
          <Tooltip title={`Player XP: ${player.xp} — Level ${player.level}`}>
            <Chip
              icon={<StarIcon />}
              label={`L${player.level}`}
              size="small"
              variant="outlined"
            />
          </Tooltip>

          {player.active_streak_days > 0 && (
            <Tooltip title={`${player.active_streak_days}-day streak`}>
              <Chip
                icon={<LocalFireDepartmentIcon />}
                label={player.active_streak_days}
                size="small"
                color="warning"
                variant="outlined"
              />
            </Tooltip>
          )}
        </>
      )}
    </Box>
  );
};

export default FortressHUD;
